import {
  createComponentMapperModel,
  withCompInfo,
} from '@wix/editor-elements-integrations';
import type { LogicalAlignment } from '@wix/editor-elements-common-utils';
import {
  withFallbackValues,
  convertPhysicalAlignmentToLogical,
  getWidthPixelValue,
} from '@wix/editor-elements-common-utils';
import type {
  GridDefinition,
  IGridMapperProps,
  GridProperties,
  GridCSSVars,
  GridPagination,
  GridSkin,
  GridHeaderColumn,
  GridStyleProperties,
} from '../Grid.types';
import { translationKeys } from '../constants';
import { convertPaginationAlignmentToFlex } from '../utils';

const skinToHeaderColumnMap: Record<GridSkin, GridHeaderColumn> = {
  GridSkin: 'none',
  GridLeftHeaderSkin: 'first',
  GridRightHeaderSkin: 'last',
};

const mapPagination = ({
  type,
  rowsPerPage,
  style,
}: GridProperties['pagination']): GridPagination => ({
  type,
  rowsPerPage,
  style,
});

function tryJsonParse<T = {}>(
  text: string | null | undefined,
  fallback: () => T,
): T {
  if (text == null) {
    return fallback();
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    return fallback();
  }
}

export const props = withCompInfo<IGridMapperProps, GridDefinition>()(
  [
    'skin',
    'compProps',
    'compData',
    'compLayout',
    'compSingleLayout',
    'topology',
    'viewMode',
    'styleProperties',
    'translate',
  ],
  ({
    skin,
    compProps: {
      columns,
      showHeader,
      pagination,
      userSelectionType,
      dateFormat,
      linkTarget,
      columnLayout,
      rowHeight,
    },
    compData: { rows, dataSource },
    compLayout,
    compSingleLayout,
    topology: { staticMediaUrl },
    styleProperties: { rowHeightUnit, columnWidthUnitMap },
    translate,
  }) => ({
    dataSource: dataSource.type,
    columns,
    columnWidthUnitMap: tryJsonParse(columnWidthUnitMap, () => ({})),
    rows,
    headerColumn: skinToHeaderColumnMap[skin],
    showHeader,
    pagination: mapPagination(pagination),
    userSelectionType,
    dateFormat,
    linkTarget,
    columnLayout,
    staticMediaUrl,
    rowHeight,
    width: getWidthPixelValue(compLayout, compSingleLayout),
    rowHeightUnit,
    translations: {
      defaultColumnLabel: translate(
        translationKeys.NAMESPACE,
        translationKeys.DEFAULT_COLUMN_LABEL,
      ),
    },
  }),
);

export const css = withCompInfo<
  GridCSSVars,
  GridDefinition,
  never,
  { styleProperties: GridStyleProperties }
>()(
  [
    'compProps',
    'compData',
    'styleProperties',
    'editorName',
    'isOneDocMigrated',
  ],
  ({
    compProps: { headerHeight, rowHeight, heightLayout },
    compData: { direction = 'inherit' },
    styleProperties: {
      cellPadding,
      rowHeightUnit,
      headerHeightUnit,
      align,
      paginationAlign,
    },
    editorName,
    isOneDocMigrated,
  }) => {
    return {
      ...(!isOneDocMigrated && heightLayout === 'auto' && { height: 'auto' }),
      ...(heightLayout === 'auto' &&
        (editorName === 'EditorX' || editorName === 'Studio') && {
          'min-height': 'auto',
        }),
      '--headerHeight':
        headerHeightUnit === 'auto' ? 'auto' : `${headerHeight}px`,
      '--rowHeight':
        rowHeightUnit === 'auto'
          ? 'auto'
          : `${rowHeight}${rowHeightUnit === 'percent' ? '%' : 'px'}`,
      '--align': align as LogicalAlignment,
      ...(align === 'start' && {
        '--cellPaddingStart': cellPadding,
      }),
      ...(align === 'end' && {
        '--cellPaddingEnd': cellPadding,
      }),
      '--paginationAlign': paginationAlign,
      '--direction': direction,
    };
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'horizontalAlignment', target: 'align' }],
        enhancer: convertPhysicalAlignmentToLogical,
      },
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'pagination', target: 'paginationAlign' }],
        enhancer: (pagination: GridProperties['pagination']) =>
          convertPaginationAlignmentToFlex(pagination.alignment),
      },
    ]),
  ],
);

export default createComponentMapperModel({ props, css });
